import React, { useState, useEffect } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { PageHeader } from "@/components/common/Primitives";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const COLORS = ["#0284c7", "#059669", "#d97706", "#2563eb", "#e11d48", "#7c3aed", "#0891b2", "#ca8a04"];

function ChartCard({ title, children, testid }) {
  return (
    <Card className="border border-slate-200/80 rounded-xl shadow-sm p-5" data-testid={testid}>
      <h3 className="text-sm font-semibold text-slate-800 mb-4">{title}</h3>
      <div className="h-64">{children}</div>
    </Card>
  );
}

function BarC({ data }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data.slice(0, 8)} layout="vertical" margin={{ left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
        <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} />
        <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fill: "#475569" }} />
        <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="#0284c7" />
      </BarChart>
    </ResponsiveContainer>
  );
}

function PieC({ data }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data.slice(0, 7)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={45}>
          {data.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default function Analytics() {
  const [a, setA] = useState(null);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get("/analytics").then(({ data }) => setA(data)).catch(() => {});
    api.get("/analytics/countries").then(({ data }) => setCountries(data)).catch(() => {});
  }, []);

  if (!a) return <div className="py-16 flex justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500" /></div>;

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Lead, RFQ, sales and country performance insights" />
      <Tabs defaultValue="leads">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="leads" data-testid="analytics-tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="rfqs" data-testid="analytics-tab-rfqs">RFQs</TabsTrigger>
          <TabsTrigger value="sales" data-testid="analytics-tab-sales">Sales</TabsTrigger>
          <TabsTrigger value="countries" data-testid="analytics-tab-countries">Countries</TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Leads by Country" testid="chart-leads-country"><BarC data={a.leads_by_country} /></ChartCard>
          <ChartCard title="Leads by Source" testid="chart-leads-source"><PieC data={a.leads_by_source} /></ChartCard>
          <ChartCard title="Leads by Product" testid="chart-leads-product"><BarC data={a.leads_by_product} /></ChartCard>
          <ChartCard title="Leads by Salesperson" testid="chart-leads-sp"><BarC data={a.leads_by_salesperson} /></ChartCard>
        </TabsContent>

        <TabsContent value="rfqs" className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="RFQs by Country" testid="chart-rfq-country"><BarC data={a.rfqs_by_country} /></ChartCard>
          <ChartCard title="RFQs by Product" testid="chart-rfq-product"><BarC data={a.rfqs_by_product} /></ChartCard>
          <Card className="border border-slate-200/80 rounded-xl shadow-sm p-6 lg:col-span-2">
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Average RFQ Value</p>
            <p className="text-3xl font-bold font-display mt-2">{fmtCurrency(a.avg_rfq_value)}</p>
          </Card>
        </TabsContent>

        <TabsContent value="sales" className="mt-6 space-y-6">
          <ChartCard title="Revenue & Gross Profit by Month" testid="chart-orders-month">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={a.orders_by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => fmtCurrency(v, false)} />
                <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Line type="monotone" dataKey="revenue" stroke="#0284c7" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="gross_profit" stroke="#059669" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Revenue by Country" testid="chart-revenue-country"><BarC data={a.revenue_by_country} /></ChartCard>
        </TabsContent>

        <TabsContent value="countries" className="mt-6">
          <Card className="border border-slate-200/80 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    {["Country", "Visitors", "Enquiries", "Qualified", "RFQs", "Quotations", "Orders", "Revenue", "Gross Profit"].map((h) => (
                      <TableHead key={h} className="text-xs uppercase tracking-wider font-semibold text-slate-500 whitespace-nowrap">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {countries.map((c) => (
                    <TableRow key={c.country} data-testid={`country-row-${c.country}`}>
                      <TableCell className="font-medium text-slate-800">{c.country}</TableCell>
                      <TableCell>{fmtNumber(c.visitors)}</TableCell>
                      <TableCell>{c.enquiries}</TableCell>
                      <TableCell>{c.qualified}</TableCell>
                      <TableCell>{c.rfqs}</TableCell>
                      <TableCell>{c.quotations}</TableCell>
                      <TableCell>{c.orders}</TableCell>
                      <TableCell className="font-medium text-slate-900">{fmtCurrency(c.revenue)}</TableCell>
                      <TableCell className="font-medium text-emerald-600">{fmtCurrency(c.gross_profit)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
